import { Controller, FieldError, FieldValues, Path } from "react-hook-form";
import { EntryList } from "../../../entry-list/EntryList";
import { FormDialog } from "../../../form-dialog/FormDialog";
import { useFormDialog } from "../../../form-dialog/FormDialog.Parts";
import { FormFieldWrapper } from "../../../form-field-wrapper/FormFieldWrapper";
import { Props } from "./EntryListField.Parts";

export const EntryListField = <
  FormFields extends FieldValues,
  FieldName extends Path<FormFields>,
>({
  control,
  errors,
  rules,
  label,
  name,
  single,
  disabled,
  emptyText,
  addDialogConfig,
  editDialogConfig,
  onAddEntries,
  onReplaceEntries,
  addActions,
  onEditEntry,
  onEntriesChange,
  addButtonContent,
  removeConfirmation,
  labelProperty,
  displayValue,
  actions,
}: Props<FormFields, FieldName>) => {
  const { openFormDialog, formDialogProps } =
    useFormDialog<Record<string, unknown>>();

  const resolvedOnAddEntries =
    onAddEntries ??
    (addDialogConfig
      ? async () => {
          const config =
            typeof addDialogConfig === "function"
              ? addDialogConfig()
              : addDialogConfig;
          const result = await openFormDialog(config);
          return result?.cleanData ? [result.cleanData] : [];
        }
      : undefined);

  const resolvedOnEditEntry =
    onEditEntry ??
    (editDialogConfig
      ? async (entry: Record<string, unknown>, index: number) => {
          const config =
            typeof editDialogConfig === "function"
              ? editDialogConfig(entry, index)
              : { ...editDialogConfig, defaultValues: entry };
          const result = await openFormDialog(config);
          return result?.cleanData;
        }
      : undefined);

  return (
    <FormFieldWrapper
      $single={single}
      name={name}
      label={label || <>&nbsp;</>}
      error={errors[name] as FieldError}
    >
      <Controller
        name={name}
        control={control}
        rules={{ ...rules }}
        render={({ field: controllerField }) => {
          const currentEntries: Record<string, unknown>[] =
            controllerField.value || [];

          const handleEntriesChange = (
            newEntries: Record<string, unknown>[],
          ) => {
            controllerField.onChange(newEntries);
            if (onEntriesChange) onEntriesChange(newEntries);
          };

          return (
            <EntryList
              initialEntries={currentEntries}
              onAddEntries={resolvedOnAddEntries}
              onReplaceEntries={onReplaceEntries}
              addActions={addActions}
              onEditEntry={resolvedOnEditEntry}
              onEntriesChange={handleEntriesChange}
              addButtonContent={addButtonContent}
              removeConfirmation={removeConfirmation}
              disabled={disabled}
              labelProperty={labelProperty}
              emptyText={emptyText}
              displayValue={displayValue}
              actions={actions}
            />
          );
        }}
      />
      {(addDialogConfig || editDialogConfig) && (
        <FormDialog {...formDialogProps} />
      )}
    </FormFieldWrapper>
  );
};
